import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Check, MessageSquare } from 'lucide-react';
import { heureCourte } from '../horaire';
import { addJours, jourCourt, jourDuMois, jourLong, lundiDe, moisCourt, moisDe, plage, type DateIso } from './dates';
import { BarreMois } from './BarreMois';
import { couleurDuPole, PastilleSeance } from './PastilleSeance';
import { depart, etendre, joursVisibles, selectionnable, semaines6, voisin } from './selection';
import type { Seance } from './seancesDemande';
import type { Nd } from './useNouvelleDemande';

const JOURS = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven'];
/** Hauteurs (px) pour décider combien de pastilles tiennent dans une case : numéro du jour, pastille à deux lignes, pastille d'une ligne, « +N ». */
const H_ENTETE = 28, H_DETAIL = 40, H_COMPACTE = 22, H_PLUS = 26;   // « +N autres » mesure 24 px au moins (cible tactile)

/** Comment les séances d'un jour tiennent dans sa case : deux lignes si la case est haute, une seule sinon, et « +N » au-delà. */
export function disposition(hauteurCase: number, nMax: number, n: number): { compacte: boolean; visibles: number } {
  const place = hauteurCase - H_ENTETE;
  const compacte = nMax * H_DETAIL > place;
  const h = compacte ? H_COMPACTE : H_DETAIL;
  if (n * h <= place) return { compacte, visibles: n };
  return { compacte: true, visibles: Math.max(1, Math.min(n, Math.floor((place - H_PLUS) / H_COMPACTE))) };
}

interface Props {
  nd: Nd;
  /** Séance dont la fenêtre est ouverte (sa pastille est marquée). */
  ouverte: string | null;
  onOuvrirSeance: (s: Seance, ou: 'c' | 'p') => void;
  /** « +N » : montrer ce jour dans le panneau. */
  onVoirJour: (d: DateIso) => void;
  /** Explication du jour survolé ou focalisé (null : revient au message par défaut). */
  onSurvol: (texte: string | null) => void;
}

export function GrilleSelection({ nd, ouverte, onOuvrirSeance, onVoirJour, onSurvol }: Props) {
  const { etat, dispatch, aujourdhui, seancesDuJour, choixEtat, meta, personnesParId, programmeDuJour } = nd;
  const sems = useMemo(() => semaines6(etat.mois), [etat.mois]);
  const visibles = useMemo(() => joursVisibles(etat.mois), [etat.mois]);
  const grille = useRef<HTMLDivElement>(null);
  const [hauteurs, setHauteurs] = useState<number[]>([]);

  // Hauteur réelle de chaque ligne de semaine : décide du nombre de pastilles et de leur forme.
  useLayoutEffect(() => {
    const g = grille.current;
    if (!g) return;
    const mesurer = () => {
      const h = Array.from(g.querySelectorAll<HTMLElement>('.nd-ligne:not(.nd-ligne--tete)')).map((r) => Math.round(r.getBoundingClientRect().height));
      setHauteurs((p) => (p.length === h.length && p.every((x, i) => x === h[i]) ? p : h));
    };
    mesurer();
    const ro = new ResizeObserver(mesurer);
    ro.observe(g);
    return () => ro.disconnect();
  }, [sems]);

  // ── Glisser (souris, tactile) et Maj + survol ─────────────────────────────────────────────────────────
  const [drag, setDrag] = useState<{ a: DateIso; b: DateIso; valeur: boolean } | null>(null);
  const dragRef = useRef(drag);
  dragRef.current = drag;
  const [apercuMaj, setApercuMaj] = useState<DateIso[]>([]);
  const jourDe = (n: EventTarget | Element | null): DateIso | null => ((n as Element | null)?.closest?.('.nd-jour') as HTMLElement | null)?.dataset.date ?? null;

  useEffect(() => {
    if (!drag) return;
    const bouge = (e: PointerEvent) => {
      const d = jourDe(document.elementFromPoint(e.clientX, e.clientY));
      if (d && dragRef.current && d !== dragRef.current.b) setDrag({ ...dragRef.current, b: d });
    };
    const fin = () => {
      const g = dragRef.current;
      setDrag(null);
      if (g) dispatch({ type: 'basculerPlage', a: g.a, b: g.b, valeur: g.valeur });   // l'aperçu a dit « ajout » ou « retrait » : le geste le fait, même si une date tapée a changé l'état entre-temps
    };
    const annule = () => setDrag(null);   // le geste est interrompu (doigt qui défile…) : rien n'est changé
    document.addEventListener('pointermove', bouge);
    document.addEventListener('pointerup', fin, { once: true });
    document.addEventListener('pointercancel', annule);
    return () => {
      document.removeEventListener('pointermove', bouge);
      document.removeEventListener('pointerup', fin);
      document.removeEventListener('pointercancel', annule);
    };
  }, [!!drag]);   // eslint-disable-line react-hooks/exhaustive-deps

  const apercuGlisse = useMemo(() => (drag ? new Set(plage(drag.a, drag.b, true).filter((d) => selectionnable(d, aujourdhui))) : null), [drag, aujourdhui]);

  // Maj enfoncée : aperçu en pointillés de la période qui serait ajoutée depuis le dernier jour ajouté.
  useEffect(() => {
    const haut = (e: KeyboardEvent) => { if (e.key === 'Shift') setApercuMaj([]); };
    document.addEventListener('keyup', haut);
    return () => document.removeEventListener('keyup', haut);
  }, []);

  const expliquer = useCallback((d: DateIso, clavier: boolean): string => {
    const raison = !selectionnable(d, aujourdhui) ? (d < aujourdhui ? `${jourLong(d)} est passé : on ne peut plus demander de remplacement pour ce jour.` : `${jourLong(d)} tombe un week-end : pas d'atelier ce jour-là.`) : null;
    if (raison) return raison;
    const ss = seancesDuJour(d);
    const choisi = etat.jours.includes(d);
    const dep = etat.absentId != null && !choisi ? depart(etat.jours, etat.ancre, d) : null;
    const tip = dep && dep !== d ? ` · ${clavier ? 'Maj + Espace' : 'Maj (⇧) + clic'} : ajouter depuis le ${jourCourt(dep)}` : '';
    // Une seule ligne (la ligne d'aide ne passe jamais à la ligne) : le nombre de séances, leur nom seulement quand il n'y en a qu'une.
    const quoi = ss.length === 0 ? (etat.absentId != null ? "pas d'atelier" : '') : ss.length === 1 ? `${ss[0].atelierNom} ${heureCourte(ss[0].debut)}` : `${ss.length} séances`;
    const geste = choisi ? ` · choisi (${clavier ? 'Espace' : 'clic'} pour retirer)` : clavier ? ' · Espace : choisir' : '';
    return `${jourLong(d)}${quoi ? ` · ${quoi}` : ''}${geste}${tip}`;
  }, [aujourdhui, seancesDuJour, etat.jours, etat.absentId, etat.ancre]);

  // Jour du dernier geste (clic, Espace) : tant que le pointeur y reste, la ligne d'aide garde le message du geste (« 1 jour ajouté… ») au lieu de redire le jour.
  const jourGeste = useRef<DateIso | null>(null);

  // ── Clavier : flèches = jour voisin ; Maj + flèches = étendre ; Espace / Entrée = choisir ou retirer ───
  const focusClavier = useRef(false);
  useLayoutEffect(() => {
    if (!focusClavier.current) return;
    focusClavier.current = false;
    // Sans « preventScroll » : en une colonne la case visée peut être sous le pli, la page défile pour la garder à l'écran (le « scroll-padding » de la feuille de style
    // laisse la place à la barre du haut). En deux colonnes la case est déjà visible : rien ne bouge.
    grille.current?.querySelector<HTMLElement>(`.nd-jour[data-date="${etat.focus}"]`)?.focus();
  });
  const surTouche = (e: React.KeyboardEvent<HTMLDivElement>) => {
    const cell = (e.target as HTMLElement).classList.contains('nd-jour') ? (e.target as HTMLElement) : null;
    if (!cell) return;
    const d = cell.dataset.date!;
    if (e.ctrlKey || e.altKey || e.metaKey) return;   // les raccourcis du navigateur (Alt + ←, Cmd + ←, Ctrl + Début…) restent au navigateur
    if (e.key === 'ArrowLeft' || e.key === 'ArrowRight' || e.key === 'ArrowUp' || e.key === 'ArrowDown') {
      e.preventDefault();
      const n = voisin(d, e.key);
      focusClavier.current = true;
      if (e.shiftKey && etat.absentId != null) dispatch({ type: 'etendreClavier', de: d, vers: n });
      if (!visibles.includes(n)) dispatch({ type: 'mois', mois: `${moisDe(n)}-01`, focus: n });
      else if (!e.shiftKey || etat.absentId == null) dispatch({ type: 'focus', d: n });
    } else if (e.key === 'Home' || e.key === 'End') {   // premier / dernier jour de la semaine
      e.preventDefault();
      const cible = e.key === 'Home' ? lundiDe(d) : addJours(lundiDe(d), 4);
      if (cible === d) return;   // déjà sur le lundi / le vendredi : rien ne change donc rien ne se redessine ; armer « focusClavier » ferait voler le focus au prochain rendu
      focusClavier.current = true;
      dispatch({ type: 'focus', d: cible });
    } else if (e.key === 'PageUp' || e.key === 'PageDown') {   // mois précédent / suivant
      e.preventDefault();
      focusClavier.current = true;
      dispatch({ type: 'delta', delta: e.key === 'PageDown' ? 1 : -1 });
    } else if (e.key === ' ' || e.key === 'Enter') {
      e.preventDefault();
      focusClavier.current = true;
      jourGeste.current = d;
      onSurvol(null);
      dispatch(e.shiftKey ? { type: 'etendre', d } : { type: 'basculerPlage', a: d, b: d, clavier: true });
    }
  };

  const surPointeur = (e: React.PointerEvent<HTMLDivElement>) => {
    if (e.button !== 0) return;
    const d = jourDe(e.target);
    if (!d) return;
    (e.currentTarget.querySelector(`.nd-jour[data-date="${d}"]`) as HTMLElement | null)?.focus({ preventScroll: true });
    jourGeste.current = d;
    onSurvol(null);   // après le focus (qui écrit l'explication du jour) : le message du geste prend la place
    if (e.shiftKey) { e.preventDefault(); dispatch({ type: 'etendre', d }); return; }
    if (!selectionnable(d, aujourdhui) || etat.absentId == null) { dispatch({ type: 'basculerPlage', a: d, b: d }); return; }
    e.preventDefault();
    setDrag({ a: d, b: d, valeur: !etat.jours.includes(d) });
  };
  const surMouvement = (e: React.MouseEvent<HTMLDivElement>) => {
    if (drag) return;
    const d = jourDe(e.target);
    if (!e.shiftKey || !d || etat.absentId == null || (e.target as HTMLElement).closest('.nd-pastille')) { if (apercuMaj.length) setApercuMaj([]); return; }
    const a = depart(etat.jours, etat.ancre, d);
    if (!a || a === d || !selectionnable(d, aujourdhui)) { if (apercuMaj.length) setApercuMaj([]); return; }
    const p = etendre(etat.jours, etat.ancre, d, aujourdhui).plage;
    setApercuMaj(p);
  };

  const nbMax = (sem: DateIso[]) => Math.max(0, ...sem.map((d) => (etat.jours.includes(d) ? seancesDuJour(d).length : 0)));
  const autreMoisVu: Record<string, true> = {};
  const moisCourant = moisDe(etat.mois);

  return (
    <>
    <BarreMois nd={nd} />
    <div
      ref={grille} className="nd-grille" role="grid" aria-labelledby="nd-mois" aria-describedby="nd-grille-aide" aria-multiselectable="true"
      onPointerDown={surPointeur} onKeyDown={surTouche} onMouseMove={surMouvement}
      onMouseLeave={() => { jourGeste.current = null; setApercuMaj([]); onSurvol(null); }}
    >
      <div className="nd-ligne nd-ligne--tete" role="row">
        {JOURS.map((j) => <div key={j} role="columnheader" className="nd-colh">{j}</div>)}
      </div>
      {sems.map((sem, i) => {
        const passee = sem.every((d) => d < aujourdhui);
        const h = hauteurs[i] ?? 140;
        const maxN = nbMax(sem);
        return (
          <div key={sem[0]} className={`nd-ligne${passee ? ' nd-passee' : ''}`} role="row">
            {sem.map((d, k) => {
              const choisi = etat.jours.includes(d);
              const passe = d < aujourdhui;
              const hors = moisDe(d) !== moisCourant;
              const ss = seancesDuJour(d);
              const disp = disposition(h, maxN, ss.length);
              const apercu = apercuGlisse?.has(d) ? (drag!.valeur ? 'ajout' : 'retrait') : apercuMaj.includes(d) ? 'ajout' : null;
              let num = String(jourDuMois(d));
              if (hors && (!autreMoisVu[moisDe(d)] || k === 0)) { autreMoisVu[moisDe(d)] = true; num += ` ${moisCourt(d)}`; }
              const label = `${jourLong(d)}${passe ? ', jour passé, non sélectionnable' : choisi ? ', choisi' : ''}${ss.length && !passe ? `, ${ss.length} séance${ss.length > 1 ? 's' : ''}` : ''}`;
              const note = etat.notesJour[d]?.trim();
              return (
                <div
                  key={d} role="gridcell" data-date={d} tabIndex={d === etat.focus ? 0 : -1} aria-current={d === aujourdhui ? 'date' : undefined} aria-selected={choisi} aria-disabled={passe || undefined} aria-label={label}
                  className={`nd-jour${choisi ? ' is-choisi' : ''}${passe ? ' is-passe' : ''}${hors ? ' is-hors' : ''}${d === aujourdhui ? ' is-auj' : ''}${apercu ? ` is-apercu-${apercu}` : ''}`}
                  onFocus={(e) => { if (e.target === e.currentTarget) { dispatch({ type: 'focus', d }); onSurvol(expliquer(d, true)); } }}
                  onClick={(e) => { if (e.detail === 0 && e.target === e.currentTarget) dispatch({ type: 'basculerPlage', a: d, b: d }); }}   // clic « d'accessibilité » : sans appui souris
                  onMouseOver={(e) => {
                    if (drag || e.shiftKey || (e.target as HTMLElement).closest('.nd-pastille') || jourGeste.current === d) return;
                    jourGeste.current = null;
                    onSurvol(expliquer(d, false));
                  }}
                >
                  <div className="nd-jour-haut">
                    <span className="nd-num">{num}</span>
                    {choisi && <span className="nd-coche"><Check size={11} aria-hidden="true" /><span>Absent·e</span></span>}
                    {d === aujourdhui && <span className="nd-auj">Aujourd'hui</span>}
                  </div>
                  {choisi ? (
                    <>
                      {ss.slice(0, disp.visibles).map((s) => (
                        <PastilleSeance
                          key={s.cle} seance={s} e={choixEtat} personnes={personnesParId} couleurPole={couleurDuPole(meta, s.poleId)}
                          ou="c" compacte={disp.compacte} ouverte={ouverte === s.cle} onOuvrir={onOuvrirSeance}
                        />
                      ))}
                      {ss.length > disp.visibles && (
                        <button
                          type="button" className="nd-plus" onPointerDown={(ev) => ev.stopPropagation()} onClick={(ev) => { ev.stopPropagation(); onVoirJour(d); }}
                          aria-label={`${ss.length - disp.visibles} autre${ss.length - disp.visibles > 1 ? 's' : ''} séance${ss.length - disp.visibles > 1 ? 's' : ''} le ${jourLong(d)} : voir dans le panneau`}
                        >
                          +{ss.length - disp.visibles} autre{ss.length - disp.visibles > 1 ? 's' : ''}
                        </button>
                      )}
                      {!ss.length && etat.absentId != null && <span className="nd-vide">{{ ok: "Pas d'atelier", chargement: 'Chargement…', erreur: 'Séances indisponibles' }[programmeDuJour(d)]}</span>}
                      {note && <span className="nd-note-j" title={note}><MessageSquare size={11} aria-hidden="true" />{note}</span>}
                    </>
                  ) : !passe && etat.absentId != null ? (
                    ss.slice(0, disp.compacte ? disp.visibles : Math.min(ss.length, 3)).map((s) => (
                      <span key={s.cle} className="nd-fantome" style={{ ['--pole' as string]: couleurDuPole(meta, s.poleId) ?? 'var(--ink-3)' }}>
                        <span className="nd-dot" aria-hidden="true" />{s.atelierNom} · {heureCourte(s.debut)}
                      </span>
                    ))
                  ) : null}
                </div>
              );
            })}
          </div>
        );
      })}
    </div>
    </>
  );
}
